import React from 'react'
import Joi from 'joi'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, EditOutlined } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { edit } from '../../store/user/actionCreator'
import { ErrorsState, latinOnly, preventCyrillicOnInput, select, validateForm } from '../../function/validation'
import { Admin } from '../../store/user/reducer'
import l from '../../page/SubscribersPageV2.module.css'
import s from './AdminsV2.module.css'

/*
 * Edit an admin — the classic PopupEditAdmin's fields and rules (first and
 * last name in Latin letters, up to 50; a department required; PUT
 * /users/edit/:id, allowed to HR and the super-admin), in the v2 look with
 * the departments as chips.
 */

const schema = Joi.object({
  firstname: latinOnly.label('First name'),
  lastname: latinOnly.label('Last name'),
  departmentId: select.label('Department'),
})

export function EditAdminDialogV2({ admin, departments, theme, onClose, onSaved }: {
  admin: Admin
  departments: { id: number; name: string }[]
  theme: string
  onClose: () => void
  onSaved: () => void
}) {
  const dispatch = useDispatch()
  const [firstname, setFirstname] = React.useState(admin.firstname || '')
  const [lastname, setLastname] = React.useState(admin.lastname || '')
  const [departmentId, setDepartmentId] = React.useState<number | 'null'>(admin.departmentId || 'null')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  function save() {
    const data = { firstname, lastname, departmentId }
    const error = validateForm(schema, data)
    if (error) { setErrors(error); return }
    setErrors(null)
    dispatch(edit(admin.id, data, onSaved))
    onClose()
  }

  const err = (k: string) => (errors as Record<string, string> | null)?.[k]
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.topIcon}><EditOutlined sx={{ fontSize: 19 }} /></span>
          <div className={s.topText}><b>Edit admin</b><span>{admin.email || admin.phone}</span></div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>
        <form className={s.form} onSubmit={(e) => { e.preventDefault(); save() }} noValidate>
          <div className={s.grid2}>
            <label className={s.field}>
              <span className={s.label}>First name</span>
              <input className={s.input} value={firstname} maxLength={50} autoFocus onChange={(e) => preventCyrillicOnInput(e, setFirstname)} />
              {err('firstname') && <span className={s.err}>{err('firstname')}</span>}
            </label>
            <label className={s.field}>
              <span className={s.label}>Last name</span>
              <input className={s.input} value={lastname} maxLength={50} onChange={(e) => preventCyrillicOnInput(e, setLastname)} />
              {err('lastname') && <span className={s.err}>{err('lastname')}</span>}
            </label>
          </div>
          <div className={s.field}>
            <span className={s.label}>Department</span>
            <div className={s.chips}>
              {departments.map((d) => (
                <button key={d.id} type="button" className={`${l.chip} ${departmentId === d.id ? l.chipOn : ''}`} onClick={() => setDepartmentId(d.id)}>{d.name}</button>
              ))}
              {!departments.length && <span className={s.hint}>Loading departments…</span>}
            </div>
            {err('departmentId') && <span className={s.err}>Choose a department</span>}
          </div>
          <div className={s.foot}>
            <span className={s.footNote}>Latin letters only</span>
            <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
            <button type="submit" className={s.primary}>Save</button>
          </div>
        </form>
      </div>
    </Dialog>
  )
}
